import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {storeInstance} from '@aofl/store/dist/legacy';
import {i18nMixin} from '@aofl/i18n-mixin';
import {
  creditedProducts,
  sdoNs,
  nodeStatuses as statuses,
  products
} from '@modules/constants-enumerate';
import '@routes/dashboard/modules/mastery-legacy/mm-teacher-dashboard/sdo';
import '@modules/session-sdo';
import '@modules/simple-modal/sdo';
import {dashboardLabels, generalLabels} from '@modules/i18n-labels';
import './sdo';
import translations from '@modules/i18n';
import {ApiService} from '@modules/api-service';
import debounce from 'debounce';
import {DashboardUtils} from '@modules/dashboard-utils';

/**
 * @summary ProgressFiltersView
 * @extends {AoflElement}
 */
@customElement('progress-filters-view')
class ProgressFiltersView extends i18nMixin(AoflElement) {
  /**
   * Creates an instance of ProgressFiltersView.
   */
  constructor() {
    super();
    this.storeInstance = storeInstance;
    this.translations = translations;
    this.searchDebounce = debounce(this.nameFilterInput, 250);
  }

  // Things are this order to prioritize the important things a teacher sees at
  // the top
  get nodeStatuses() {
    const base = [
      statuses.INTERVENTION,
      statuses.IN_PROGRESS,
      statuses.PRIOR_KNOWLEDGE,
      statuses.CREDITED,
      statuses.MASTERY_ACHIEVED,
    ];
    return creditedProducts.includes(this.product)
      ? base
      : base.filter((s) => s !== statuses.CREDITED);
  }

  get nodeStatusInfo() {
    const all = {
      intervention: dashboardLabels.STRUGGLING,
      in_progress: dashboardLabels.IN_PROGRESS,
      mastery_achieved: dashboardLabels.COMPLETED,
      credited: dashboardLabels.CREDITED,
      prior_knowledge: dashboardLabels.PRIOR_KNOWLEDGE,
    };
    if (!creditedProducts.includes(this.product)) {
      const {credited, ...rest} = all;
      return rest;
    }
    return all;
  }

  @property({type: Object, mapState: `${sdoNs.PROGRESS_FILTERS_VIEW}.filters`, store: storeInstance})
  filters = {};

  @property({type: Array, mapState: `${sdoNs.PROGRESS_FILTERS_VIEW}.$standardSets`, store: storeInstance})
  standardSets = [];

  @property({type: Array, mapState: `${sdoNs.PROGRESS_FILTERS_VIEW}.loParents`, store: storeInstance})
  loParents = [];

  @property({type: Array, mapState: `${sdoNs.PROGRESS_FILTERS_VIEW}.grades`, store: storeInstance})
  grades = [];

  @property({type: String})
  selectedStatus = dashboardLabels.SELECT_A_STATUS;

  @property({type: Object, mapState: `${sdoNs.PROGRESS_FILTERS_VIEW}.$selectedStandardSet`, store: storeInstance})
  selectedStandardSet = {};

  @property({type: Array, mapState: `${sdoNs.PROGRESS_FILTERS_VIEW}.$loParentsSelectedCategory`, store: storeInstance})
  selectedStandardCategory = '';

  @property({type: Boolean, attribute: true})
  ['for-student-view'] = false;

  @property({type: Boolean, attribute: true})
  ['autoselect-standard-set'] = false;

  @property({type: String, attribute: true})
  product = '';

  @property({type: Boolean, attribute: true})
  ['prevent-reset'] = false;

  async connectedCallback() {
    super.connectedCallback();

    let standardSetFilter = null;

    await this.getNodeExternalStandards();
    if (this['for-student-view']) {
        const resp = await this.searchSkills();
        this.updateLOParentsDropdowns(resp);
    } else {
      if (this['autoselect-standard-set']) {
        const state = storeInstance.getState();
        const externalStandards = state[sdoNs.SESSION].settings?.external_standards || [];
        standardSetFilter = externalStandards[0] || null;
      }
      await this.setStandardSetFilter(standardSetFilter);
    }
  }

  async getNodeExternalStandards() {
    const state = storeInstance.getState();
    const externalStandards = state[sdoNs.SESSION].settings?.external_standards || [];
    if (externalStandards.length) {
      const resp = await ApiService.activityMetadataGetKnowledgeComponent(externalStandards, this.product);
      storeInstance.commit(
        {
          namespace: sdoNs.PROGRESS_FILTERS_VIEW,
          mutationId: 'setExternalStandardSets',
          payload: resp.results
        },
      );
    }
  }

  setNameFilter(e) {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = (formData.get('name') || '').trim();
    this.storeInstance.commit({
      namespace: sdoNs.PROGRESS_FILTERS_VIEW,
      mutationId: 'setNameFilter',
      payload: name
    });
    this.sendFilterChangeEvent('name', name);
  }

  nameFilterInputDebounce(e) {
    this.searchDebounce(e.target.value);
  }

  nameFilterInput(value) {
    const name = (value).trim();
    this.storeInstance.commit({
      namespace: sdoNs.PROGRESS_FILTERS_VIEW,
      mutationId: 'setNameFilter',
      payload: name
    });
    this.searchSkills();
    this.sendFilterChangeEvent('name', name);
  }

  setStatusFilter(filter) {
    const status = (filter || '').trim().toLowerCase();
    this.selectedStatus = this.nodeStatusInfo[filter] || generalLabels.all;
    this.storeInstance.commit({
      namespace: sdoNs.PROGRESS_FILTERS_VIEW,
      mutationId: 'setStatusFilter',
      payload: status
    });
    this.sendFilterChangeEvent('status', status);
  }

  async setGradeFilter(filter) {
    this.storeInstance.commit(
      {namespace: sdoNs.PROGRESS_FILTERS_VIEW, mutationId: 'setGradeFilter', payload: filter},
    );
    const resp = await this.searchSkills();
    this.updateLOParentsDropdowns(resp);
    this.sendFilterChangeEvent('grade', filter);
  }

  updateGradesDropdowns(resp) {
    this.setStandardCategoryFilter(null);
    let gradesBucket = [];

    // check if org has external standards
    // if exists, then read from external standards filtered grades
    if (resp?.aggregations?.external_standards?.doc_count > 0) {
      gradesBucket = resp?.aggregations?.external_standards?.filtered?.grade?.buckets || [];
    } else {
      // else read from grades
      gradesBucket = resp?.aggregations?.grades?.buckets || [];
    }
    storeInstance.commit({
      namespace: sdoNs.PROGRESS_FILTERS_VIEW,
      mutationId: 'setSkillCountsPerGrade',
      payload: gradesBucket
    });

    this.updateLOParentsDropdowns(resp);
  }

  updateLOParentsDropdowns(resp, updateLOCategories = true) {
    if (this.standardSets.length > 0) {
      const loParentsBucket = resp?.aggregations?.external_standards?.filtered?.lo_parent_name?.buckets || [];
      const loParents = loParentsBucket.map((item) => item.key);
      const loParentsCategory = resp?.aggregations?.external_standards?.filtered?.lo_parent_type?.buckets[0]?.key.trim() || '';
      const commits = [{namespace: sdoNs.PROGRESS_FILTERS_VIEW, mutationId: 'setLoParentsCategory', payload: loParentsCategory}];
      if (updateLOCategories) {
        commits.push({namespace: sdoNs.PROGRESS_FILTERS_VIEW, mutationId: 'setLoParents', payload: loParents});
      }
      this.storeInstance.commit(...commits);
    }
  }

  async setStandardSetFilter(filter) {
    const standardSetFilter = filter || '';
    this.storeInstance.commit(
      {namespace: sdoNs.PROGRESS_FILTERS_VIEW, mutationId: 'setStandardSetFilter', payload: standardSetFilter},
    );
    const resp = await this.searchSkills();
    this.updateGradesDropdowns(resp);

    this.sendFilterChangeEvent('standardSet', filter);
  }

  async searchSkills() {
    this.storeInstance.commit(
      {namespace: sdoNs.PROGRESS_FILTERS_VIEW, mutationId: 'setSkillsLoaded', payload: false},
    );
    let product = this.product;
    // Skills on db are stored as my_reading_academy and my_math_academy.
    // for the legacy products, we point to these but have to hardcode
    // like so
    if (this.product === products.MATH_ACADEMY) {
      product = products.MY_MATH_ACADEMY;
    } else if (this.product === products.READING_MASTERY) {
      product = products.MY_READING_ACADEMY;
    }
    const resp = await DashboardUtils.skillsSearchCall(product, ['id', 'name', 'grades']);
    this.storeInstance.commit(
      {namespace: sdoNs.PROGRESS_FILTERS_VIEW, mutationId: 'setSkillInfo', payload: resp.results},
      {namespace: sdoNs.PROGRESS_FILTERS_VIEW, mutationId: 'setSkillsLoaded', payload: true}
    );
    return resp;
  }

  async setStandardCategoryFilter(filter) {
    this.selectedStandardCategory = (filter === null) ? generalLabels.all : filter;
    this.storeInstance.commit({
      namespace: sdoNs.PROGRESS_FILTERS_VIEW,
      mutationId: 'setStandardCategoryFilter',
      payload: (filter === null) ? '' : filter
    });
    const resp = await this.searchSkills();
    this.updateLOParentsDropdowns(resp, false);
    this.sendFilterChangeEvent('standardSetCategory', filter);
  }

  setActiveThisWeek(e) {
    const checked = e.target.checked;
    this.storeInstance.commit({
      namespace: sdoNs.PROGRESS_FILTERS_VIEW,
      mutationId: 'setActiveThisWeekFilter',
      payload: checked
    });
    this.sendFilterChangeEvent('activeThisWeek', checked);
  }

  resetFilters() {
    this.selectedStatus = dashboardLabels.SELECT_A_STATUS;
    if (this.standardSets.length > 1) {
      this.selectedStandardSet = dashboardLabels.SELECT_A_STANDARD_SET;
      this.selectedStandardCategory = '';
    }

    if (this['for-student-view']) {
      this.storeInstance.commit(
        {namespace: sdoNs.PROGRESS_FILTERS_VIEW, mutationId: 'resetFiltersExceptSet'},
      );
    } else {
      this.storeInstance.commit(
        {namespace: sdoNs.PROGRESS_FILTERS_VIEW, mutationId: 'resetFilters'},
      );
    }
    this.setStandardSetFilter(this.filters.standardSetFilter);
  }

  sendFilterChangeEvent(key, value) {
    this.dispatchEvent(new CustomEvent('filter-change', {
      detail: {
        key,
        value
      },
      composed: true
    }));
  }

  disconnectedCallback() {
    if (!this['prevent-reset']) {
      this.resetFilters();
    }
    super.disconnectedCallback();
  }

  /**
   * @readonly
   */
  static is = 'progress-filters-view';

  /**
   * @return {Object}
   */
  render() {
    return super.render({
      default: {
        template,
        styles: [styles]
      }
    });
  }
}

export default ProgressFiltersView;
